import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'react-hot-toast';
import { App } from './App';
import { sollWiederholen } from '@sizilianer/core/apiFetch';
// Seiteneffekt: konfiguriert apiFetch VOR jedem Feature-Import (0.7).
import './lib/apiClient';
import { initSentry } from './lib/sentry';
import { reloadOnceForChunkError } from './utils/lazyPage';
import './index.css';
import './styles/sizilianer-design.css';

// Sentry-Init MUSS vor React rendern laufen — sonst keine init-time-Errors.
initSentry();

// Nach einem Deploy tragen Assets neue Hashes — Vite meldet fehlgeschlagene
// Modul-Preloads über dieses Ereignis. Ein Reload holt das frische index.html
// und heilt es; der Guard in reloadOnceForChunkError verhindert Schleifen.
window.addEventListener('vite:preloadError', (event) => {
  if (reloadOnceForChunkError()) event.preventDefault();
});

/**
 * Hinweis: <title>, <meta>, <link> werden in React 19 nativ
 * unterstützt — direkt im Component-Tree platzieren, React hebt
 * sie automatisch in den <head>. Kein react-helmet nötig.
 */

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60_000, // 1 min
      gcTime: 5 * 60_000, // 5 min
      refetchOnWindowFocus: false,
      // ⚠️ Global, nicht je Hook: `retry: 1` war eine Lücke in der
      // VOREINSTELLUNG, die jede künftige Abfrage geerbt hätte. Ein 4xx wird
      // durch Wiederholen nicht besser — bei einer Kachel, die dauerhaft 403
      // bekommt, verdoppelte die alte Regel jede Anfrage und hielt den
      // Netzverkehr nie still (halbe Wurzel von BF-3). Reine Verengung: wo
      // kein Status erkennbar ist, verhält sie sich byte-identisch wie vorher.
      retry: sollWiederholen,
    },
  },
});

const rootEl = document.getElementById('root');
if (!rootEl) throw new Error('Root element #root not found');

createRoot(rootEl).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <App />
        {/* Kein Cookie-Banner: Fonts self-hosted, Karte statisch, kein
            Tracking-by-default (§ 25 Abs. 2 TDDDG — bannerfrei). Ohne
            Einwilligung setzt die Seite KEINEN Cookie. Der einzige
            einwilligungspflichtige Drittdienst (Baroon-Chat-Widget) holt
            seine Einwilligung per Zwei-Klick direkt am Widget ein
            (ChatbotConsentLauncher, § 25 Abs. 1 TDDDG + Art. 6 Abs. 1
            lit. a DSGVO) — dafür ist weiterhin kein Banner nötig. ERST ab
            dieser Zustimmung kommt ein eigener Sitzungs-Cookie dazu
            (sz_chat, Path=/api/chatbot, HttpOnly, 12 h) und der
            Seitenverlauf wird erfasst; beides hängt strikt am Consent und
            steht in der Datenschutzerklärung (Abschnitt 7). Kommt Analytics
            (Plausible/GA4) dazu, braucht es einen echten Consent-Flow. */}
        <Toaster
          position="top-right"
          toastOptions={{
            duration: 5000,
            style: {
              background: 'var(--color-paper-2)',
              color: 'var(--color-ink)',
              border: '1px solid var(--color-rule)',
              fontFamily: 'var(--font-sans)',
            },
          }}
        />
      </BrowserRouter>
    </QueryClientProvider>
  </StrictMode>,
);
